import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, CheckCircle2 } from "lucide-react";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function RegisterPage() {
  const navigate = useNavigate();
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    full_name: "",
    email: "",
    phone: "",
    budget_range: "",
    property_type: "",
    timeline: "",
    current_address: "",
    employment_status: "",
    first_time_buyer: "",
  });

  const updateField = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.email || !form.phone) {
      toast.error("Please fill in all required fields");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/register`, {
        ...form,
        first_time_buyer: form.first_time_buyer === "yes",
      });
      setSubmitted(true);
      toast.success("Registration submitted successfully");
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div
        data-testid="registration-success"
        className="min-h-screen flex items-center justify-center px-6"
        style={{ background: "#050505" }}
      >
        <div className="text-center max-w-lg">
          <CheckCircle2 size={48} strokeWidth={1} style={{ color: "#D4AF37" }} className="mx-auto mb-8" />
          <h2
            className="text-4xl md:text-5xl font-normal tracking-tight mb-6"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            Thank <span className="italic" style={{ color: "#D4AF37" }}>You</span>
          </h2>
          <p
            className="text-base leading-relaxed mb-10"
            style={{ color: "#A3A3A3", fontFamily: "'Manrope', sans-serif" }}
          >
            Your interest has been registered. Our team will be in touch
            shortly with exclusive updates about Elysian Heights, Coulsdon.
          </p>
          <Link
            to="/"
            data-testid="success-home-btn"
            className="inline-flex items-center gap-3 px-8 py-4 text-xs tracking-[0.2em] uppercase font-semibold transition-all duration-300 hover:-translate-y-1"
            style={{ background: "#D4AF37", color: "#000", fontFamily: "'Manrope', sans-serif" }}
          >
            Return Home
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="register-page" className="min-h-screen pt-20" style={{ background: "#050505" }}>
      {/* Header */}
      <div className="max-w-[800px] mx-auto px-6 md:px-12 pt-12 md:pt-20">
        <Link
          to="/"
          data-testid="register-back-btn"
          className="inline-flex items-center gap-2 text-sm mb-10 transition-colors duration-300"
          style={{ color: "#A3A3A3", fontFamily: "'Manrope', sans-serif" }}
        >
          <ArrowLeft size={16} strokeWidth={1} />
          Back to Home
        </Link>

        <p
          className="text-xs tracking-[0.2em] uppercase font-bold mb-4"
          style={{ color: "#D4AF37", fontFamily: "'Manrope', sans-serif" }}
        >
          Express Your Interest
        </p>
        <h1
          className="text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tight leading-none mb-6"
          style={{ fontFamily: "'Playfair Display', serif" }}
        >
          Register <span className="italic" style={{ color: "#D4AF37" }}>Today</span>
        </h1>
        <p
          className="text-base md:text-lg leading-relaxed mb-12"
          style={{ color: "#A3A3A3", fontFamily: "'Manrope', sans-serif" }}
        >
          Complete the form below to secure your place on our priority list and
          receive exclusive invitations to private viewings.
        </p>
        <div className="section-line mb-12" />
      </div>

      {/* Form */}
      <form
        onSubmit={handleSubmit}
        data-testid="registration-form"
        className="max-w-[800px] mx-auto px-6 md:px-12 pb-24"
      >
        {/* Personal Details */}
        <div className="mb-16">
          <p
            className="text-xs tracking-[0.2em] uppercase font-bold mb-8"
            style={{ color: "#D4AF37", fontFamily: "'Manrope', sans-serif" }}
          >
            Personal Details
          </p>
          <div className="flex flex-col gap-8">
            <div>
              <Label className="text-xs tracking-[0.15em] uppercase mb-3 block" style={{ color: "#A3A3A3" }}>
                Full Name *
              </Label>
              <Input
                data-testid="input-full-name"
                value={form.full_name}
                onChange={(e) => updateField("full_name", e.target.value)}
                placeholder="Enter your full name"
                className="luxury-input h-12 text-base text-white placeholder:text-white/30 border-0 rounded-none bg-white/[0.03]"
                style={{ borderBottom: "1px solid rgba(255,255,255,0.15)" }}
                required
              />
            </div>
            <div>
              <Label className="text-xs tracking-[0.15em] uppercase mb-3 block" style={{ color: "#A3A3A3" }}>
                Email Address *
              </Label>
              <Input
                data-testid="input-email"
                type="email"
                value={form.email}
                onChange={(e) => updateField("email", e.target.value)}
                placeholder="your@email.com"
                className="luxury-input h-12 text-base text-white placeholder:text-white/30 border-0 rounded-none bg-white/[0.03]"
                style={{ borderBottom: "1px solid rgba(255,255,255,0.15)" }}
                required
              />
            </div>
            <div>
              <Label className="text-xs tracking-[0.15em] uppercase mb-3 block" style={{ color: "#A3A3A3" }}>
                Phone Number *
              </Label>
              <Input
                data-testid="input-phone"
                type="tel"
                value={form.phone}
                onChange={(e) => updateField("phone", e.target.value)}
                placeholder="+44 7XXX XXXXXX"
                className="luxury-input h-12 text-base text-white placeholder:text-white/30 border-0 rounded-none bg-white/[0.03]"
                style={{ borderBottom: "1px solid rgba(255,255,255,0.15)" }}
                required
              />
            </div>
            <div>
              <Label className="text-xs tracking-[0.15em] uppercase mb-3 block" style={{ color: "#A3A3A3" }}>
                Current Address
              </Label>
              <Input
                data-testid="input-address"
                value={form.current_address}
                onChange={(e) => updateField("current_address", e.target.value)}
                placeholder="Your current address"
                className="luxury-input h-12 text-base text-white placeholder:text-white/30 border-0 rounded-none bg-white/[0.03]"
                style={{ borderBottom: "1px solid rgba(255,255,255,0.15)" }}
              />
            </div>
          </div>
        </div>

        {/* Property Preferences */}
        <div className="mb-16">
          <p
            className="text-xs tracking-[0.2em] uppercase font-bold mb-8"
            style={{ color: "#D4AF37", fontFamily: "'Manrope', sans-serif" }}
          >
            Property Preferences
          </p>
          <div className="flex flex-col gap-8">
            <div>
              <Label className="text-xs tracking-[0.15em] uppercase mb-3 block" style={{ color: "#A3A3A3" }}>
                Budget Range
              </Label>
              <Select value={form.budget_range} onValueChange={(v) => updateField("budget_range", v)}>
                <SelectTrigger
                  data-testid="select-budget"
                  className="h-12 text-base rounded-none border-0 bg-white/[0.03] text-white"
                  style={{ borderBottom: "1px solid rgba(255,255,255,0.15)" }}
                >
                  <SelectValue placeholder="Select your budget range" />
                </SelectTrigger>
                <SelectContent className="bg-[#1A1A1A] border-[#333] text-white">
                  <SelectItem value="under-300k">Under &pound;300,000</SelectItem>
                  <SelectItem value="300k-500k">&pound;300,000 - &pound;500,000</SelectItem>
                  <SelectItem value="500k-750k">&pound;500,000 - &pound;750,000</SelectItem>
                  <SelectItem value="750k-1m">&pound;750,000 - &pound;1,000,000</SelectItem>
                  <SelectItem value="over-1m">Over &pound;1,000,000</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs tracking-[0.15em] uppercase mb-3 block" style={{ color: "#A3A3A3" }}>
                Property Type
              </Label>
              <Select value={form.property_type} onValueChange={(v) => updateField("property_type", v)}>
                <SelectTrigger
                  data-testid="select-property-type"
                  className="h-12 text-base rounded-none border-0 bg-white/[0.03] text-white"
                  style={{ borderBottom: "1px solid rgba(255,255,255,0.15)" }}
                >
                  <SelectValue placeholder="Select property type" />
                </SelectTrigger>
                <SelectContent className="bg-[#1A1A1A] border-[#333] text-white">
                  <SelectItem value="houses">Houses</SelectItem>
                  <SelectItem value="flats-apartments">Flats / Apartments</SelectItem>
                  <SelectItem value="bungalows">Bungalows</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs tracking-[0.15em] uppercase mb-3 block" style={{ color: "#A3A3A3" }}>
                Purchase Timeline
              </Label>
              <Select value={form.timeline} onValueChange={(v) => updateField("timeline", v)}>
                <SelectTrigger
                  data-testid="select-timeline"
                  className="h-12 text-base rounded-none border-0 bg-white/[0.03] text-white"
                  style={{ borderBottom: "1px solid rgba(255,255,255,0.15)" }}
                >
                  <SelectValue placeholder="When are you looking to purchase?" />
                </SelectTrigger>
                <SelectContent className="bg-[#1A1A1A] border-[#333] text-white">
                  <SelectItem value="asap">As soon as possible</SelectItem>
                  <SelectItem value="3-months">Within 3 months</SelectItem>
                  <SelectItem value="6-months">Within 6 months</SelectItem>
                  <SelectItem value="12-months">Within 12 months</SelectItem>
                  <SelectItem value="just-browsing">Just browsing</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        {/* Additional Information */}
        <div className="mb-16">
          <p
            className="text-xs tracking-[0.2em] uppercase font-bold mb-8"
            style={{ color: "#D4AF37", fontFamily: "'Manrope', sans-serif" }}
          >
            Additional Information
          </p>
          <div className="flex flex-col gap-8">
            <div>
              <Label className="text-xs tracking-[0.15em] uppercase mb-3 block" style={{ color: "#A3A3A3" }}>
                Employment Status
              </Label>
              <Select value={form.employment_status} onValueChange={(v) => updateField("employment_status", v)}>
                <SelectTrigger
                  data-testid="select-employment"
                  className="h-12 text-base rounded-none border-0 bg-white/[0.03] text-white"
                  style={{ borderBottom: "1px solid rgba(255,255,255,0.15)" }}
                >
                  <SelectValue placeholder="Select employment status" />
                </SelectTrigger>
                <SelectContent className="bg-[#1A1A1A] border-[#333] text-white">
                  <SelectItem value="employed">Employed</SelectItem>
                  <SelectItem value="self-employed">Self-Employed</SelectItem>
                  <SelectItem value="retired">Retired</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs tracking-[0.15em] uppercase mb-3 block" style={{ color: "#A3A3A3" }}>
                First-time Buyer?
              </Label>
              <Select value={form.first_time_buyer} onValueChange={(v) => updateField("first_time_buyer", v)}>
                <SelectTrigger
                  data-testid="select-first-time"
                  className="h-12 text-base rounded-none border-0 bg-white/[0.03] text-white"
                  style={{ borderBottom: "1px solid rgba(255,255,255,0.15)" }}
                >
                  <SelectValue placeholder="Are you a first-time buyer?" />
                </SelectTrigger>
                <SelectContent className="bg-[#1A1A1A] border-[#333] text-white">
                  <SelectItem value="yes">Yes</SelectItem>
                  <SelectItem value="no">No</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        {/* Submit */}
        <button
          type="submit"
          data-testid="submit-registration-btn"
          disabled={loading}
          className="w-full py-4 text-xs tracking-[0.2em] uppercase font-semibold transition-all duration-300 hover:-translate-y-1 disabled:opacity-50 disabled:cursor-not-allowed"
          style={{ background: "#D4AF37", color: "#000", fontFamily: "'Manrope', sans-serif" }}
        >
          {loading ? "Submitting..." : "Submit Registration"}
        </button>

        <p
          className="text-xs text-center mt-6"
          style={{ color: "#666", fontFamily: "'Manrope', sans-serif" }}
        >
          By submitting, you agree to receive communications regarding Elysian Heights.
        </p>
      </form>
    </div>
  );
}
